import { Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { AWS_BUCKET_URL } from '../../toolkit/constants';
import BetTitleAndBox from './bet_title_and_box.js';
import {
  gameDateToMatchDay,
  gameDateToMatchTime,
  getMatchTime,
  checkHasMarketVariable,
  sportTypeToSportId,
  isMaradonaSpecifier
} from 'toolkit/utils.js';
import * as _ from 'lodash';
import { getTeamNameHelper } from '../../helper/matchDetailHelper';
import { EnumEventStatus, EnumMatchStatusName } from 'toolkit/Enums';
import Jersey from 'components/jersey';
import { toggleFavorite } from '../../stores/match/index.js';
import { showToaster } from '../../mixin/general.js';

function MatchCard(props) {
  const { t } = useTranslation('translations', { useSuspense: false });
  const dispatch = useDispatch();
  const selectedLocale = useSelector((state) => state.match.selectedLocale);
  const locales = useSelector((state) => state.match.locales);
  const { providerId } = props;
  if (!providerId) return null;
  const match = useSelector((state) => state.match.events[providerId]);
  if (!match || match.isMatch === false) return null;

  const handleFavoriteClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (match && match.providerId && match.sport !== undefined) {
      const wasFavorite = !!match.isFavorite;

      try {
        const result = await dispatch(
          toggleFavorite({ gameNumber: match.providerId, sportType: match.sport })
        ).unwrap();

        if (result?.success && !wasFavorite && result?.isFavorite === false) {
          const errorMessage =
            result?.message ||
            t('components.matchcard.favoriteLimitReached') ||
            'Failed to add to favorites. Favorite list limit reached';
          showToaster(errorMessage, 'error');
          return;
        }

        if (result?.success) {
          const defaultMessage = wasFavorite
            ? t('components.matchcard.favoriteRemoved') || 'Removed from favorites'
            : t('components.matchcard.favoriteAdded') || 'Added to favorites';

          const successMessage = result?.message || defaultMessage;
          showToaster(successMessage, 'success');
        } else {
          const errorMessage =
            result?.message ||
            t('components.matchcard.favoriteError') ||
            'Failed to update favorite';
          showToaster(errorMessage, 'error');
        }
      } catch (error) {
        const errorMessage =
          error?.message || t('components.matchcard.favoriteError') || 'Failed to update favorite';
        showToaster(errorMessage, 'error');
      }
    }
  };

  const defaultMarketObject = {
    1: ['1', 'X', '2'],
    7: ['1', 'X', '2'],
    8: ['1', 'X', '2'],
    10: ['1X', '12', 'X2'],
    14: ['1', 'X', '2'],
    18: ['+', '-'],
    29: ['Yes', 'No'],
    113: ['1', 'X', '2'],
    114: ['1', 'X', '2'],
    186: ['1', '2']
  };

  const sortByMarketId = (arr) => {
    return _.orderBy(arr, (market) => {
      if (market === null || market === undefined) return false;
      const marketVariable = checkHasMarketVariable(market.id, market.stringId);
      if (marketVariable) {
        if (!isNaN(Number(marketVariable))) return Number(marketVariable);
        return marketVariable;
      }
      return false;
    });
  };

  const getDefaultMarket = () => {
    // hide bettitle if match card doesn't have the selected market
    if (props.hideIfNoMarket) {
      return null;
    }
    let defaultMarket;
    const winnerBet = match.markets.find((m) => m.id === 1);
    if (winnerBet) {
      defaultMarket = winnerBet;
    } else {
      const otherWinningIds = [7, 113, 114, 186, 219, 340, 251];
      defaultMarket = match.markets.find((m) => otherWinningIds.includes(m.id));
    }
    if (!defaultMarket) {
      defaultMarket = {
        id: 186,
        name: '',
        outcomeType: '',
        stringId: '',
        outcomes: defaultMarketObject[186],
        renderNull: true
      };
    }
    return defaultMarket;
  };

  function MatchCardGetTitle() {
    const sportType = match.sport;
    const providerId = match.providerId;
    let marketType = props.marketType;
    let markets = [];
    if (!match.markets || match.markets.length === 0) {
      return (
        <div className="d-flex justify-content-center align-item-center">
          {t('components.matchcard.noMarket')}
        </div>
      );
    } else if (sportType === 0) {
      if (
        match.matchStatusId == EnumMatchStatusName.FIRST_EXTRA ||
        match.matchStatusId == EnumMatchStatusName.SECOND_EXTRA ||
        match.matchStatusId === EnumMatchStatusName.AWAITING_EXTRA_TIME ||
        match.matchStatusId === EnumMatchStatusName.EXTRA_TIME_HALFTIME
      ) {
        if (marketType === 7 && match.markets.find((x) => x.id === 114)) {
          marketType = 114;
        } else if (marketType === 1 && match.markets.find((x) => x.id === 113)) {
          marketType = 113;
        } else if (marketType === 8 && match.markets.find((x) => x.id === 115)) {
          marketType = 115;
        }
      }

      const filteredMarkets = match.markets.filter((x) => x.id === marketType);
      if (filteredMarkets.length === 0) {
        if (props.lockIfNoMarket) {
          markets.push({
            id: marketType,
            name: '',
            outcomeType: '',
            stringId: '',
            outcomes: defaultMarketObject[marketType],
            renderNull: true
          });
        } else {
          if (marketType === 7 && match.sport === 0 && match.matchStatusId === 0) {
            markets.push(getDefaultMarket());
          } else {
            markets.push({
              id: marketType,
              name: '',
              outcomeType: '',
              stringId: '',
              outcomes: defaultMarketObject[marketType],
              renderNull: true
            });
          }
        }
      } else {
        markets.push(...filteredMarkets);
      }
    } else {
      markets.push(getDefaultMarket());
    }

    markets = markets.sort(
      (t1, t2) => Number(t1.stringId.split('-S:')[1]) - Number(t2.stringId.split('-S:')[1])
    );

    if (marketType === 14) {
      markets = sortByMarketId(markets);
    }

    if (props.isMaradonaHandicap && markets.length > 0) {
      const maradonaMarkets = markets.filter((m) => isMaradonaSpecifier(m.stringId));
      if (maradonaMarkets.length > 0) {
        // Show both the home (e.g. 9:0) and away (e.g. 0:9) handicaps so they
        // render as separate rows stacked vertically, with the home one first.
        const isHomeHandicap = (m) => (m.stringId.split('-S:')[1] || '').endsWith(':0');
        const homeMarkets = maradonaMarkets.filter(isHomeHandicap);
        const awayMarkets = maradonaMarkets.filter((m) => !isHomeHandicap(m));
        const selected = [];
        if (homeMarkets.length > 0) selected.push(homeMarkets[homeMarkets.length - 1]);
        if (awayMarkets.length > 0) selected.push(awayMarkets[awayMarkets.length - 1]);
        markets = selected.length > 0 ? selected : [maradonaMarkets[maradonaMarkets.length - 1]];
      } else {
        markets = [markets[markets.length - 1]];
      }
    }

    let buttonBg = '';
    if (match?.eventStatus > EnumEventStatus.NOTSTARTED) {
      if (match.matchStatusId === EnumMatchStatusName.HALFTIME) {
        buttonBg = 'bg-button-ht';
      } else {
        buttonBg = 'bg-button-will-live';
      }
    } else {
      buttonBg = 'bg-button-will-no-live';
    }

    return markets.map((market, index) => (
      <BetTitleAndBox
        key={index}
        market={market}
        matchId={providerId}
        sportType={sportType}
        lockIfNoMarket={props.lockIfNoMarket}
        isLastMarket={index === markets.length - 1}
        isMaradonaHandicap={props.isMaradonaHandicap}
        betBox={buttonBg}
      />
    ));
  }

  // if (!getDefaultMarket()) {
  //   return <div></div>;
  // }

  const servingBall =
    match.sport == sportTypeToSportId('Volleyball') ? (
      <img className="serving-ball" src={`${AWS_BUCKET_URL}/Icons/182.png`} />
    ) : (
      <img className="serving-ball" src={`${AWS_BUCKET_URL}/Icons/118.png`} />
    );
  const homeScores = [];
  const awayScores = [];
  const periodScoresLenght = match.periodScores.length;
  match.periodScores.forEach((element, index) => {
    if (index === periodScoresLenght - 1) {
      homeScores.push(
        <span className="text-success" key={`homeScore1 ${index}`}>
          {element.homeScore}
        </span>
      );
      awayScores.push(
        <span className="text-success" key={`awayScore1 ${index}`}>
          {element.awayScore}
        </span>
      );
    } else {
      homeScores.push(<span key={`homeScore2 ${index}`}>{element.homeScore} </span>);
      awayScores.push(<span key={`awayScore2 ${index}`}>{element.awayScore} </span>);
    }
  });

  const homeRedCardCount = (
    <span className="badge bg-danger ms-2 px-1">{match.homeRedCardCount}</span>
  );
  const awayRedCardCount = (
    <span className="badge bg-danger ms-2 px-1">{match.awayRedCardCount}</span>
  );
  const getTeam = (team) => {
    const lastPeriodScore = match.periodScores[match.periodScores.length - 1];
    const homeServingBall =
      lastPeriodScore && lastPeriodScore.homeScore > lastPeriodScore.awayScore;
    const awayShowServingBall =
      lastPeriodScore && lastPeriodScore.homeScore <= lastPeriodScore.awayScore;
    const redCardCountSource = team == 'team1' ? 'homeRedCardCount' : 'awayRedCardCount';

    const teamName = getTeamNameHelper(
      locales,
      selectedLocale,
      match.competitors[team == 'team1' ? 0 : 1],
      t
    );
    const showServingBall =
      team == 'team1' ? homeServingBall && servingBall : awayShowServingBall && servingBall;
    return (
      <span>
        {teamName} {showServingBall}
        {match[redCardCountSource] > 0
          ? team == 'team1'
            ? homeRedCardCount
            : awayRedCardCount
          : null}
      </span>
    );
  };

  const getScore = (team) => {
    if (match.eventStatus == EnumEventStatus.RUNNING) {
      if (
        match.sport == sportTypeToSportId('Volleyball') ||
        match.sport == sportTypeToSportId('Tennis') ||
        match.sport == sportTypeToSportId('TableTennis')
      ) {
        return <span className="float-end">{team == 'team1' ? homeScores : awayScores}</span>;
      } else {
        return (
          <span className="float-end">{team == 'team1' ? match.homeScore : match.awayScore}</span>
        );
      }
    } else {
      return null;
    }
  };

  const getTeamSolo = (team) => {
    const redCardCountSource = team == 'team1' ? 'homeRedCardCount' : 'awayRedCardCount';
    const teamName = getTeamNameHelper(
      locales,
      selectedLocale,
      match.competitors[team == 'team1' ? 0 : 1],
      t
    );
      const fallbackColor = team == 'team1'
      ? 'black'
      : 'white';
    return (
      <div className="d-flex align-items-center gap-1">
        <Jersey
          width={16}
          height={16}
          jersey={match.competitors[team == 'team1' ? 0 : 1].jersey}
          color={fallbackColor}
        />
        {match.sport == 13 || match.sport == 2 || match.sport == 12 ? (
          getTeam(team)
        ) : (
          <span>
            {teamName}
            {match[redCardCountSource] > 0
              ? team == 'team1'
                ? homeRedCardCount
                : awayRedCardCount
              : null}
          </span>
        )}
      </div>
    );
  };

  const getMatchCardDayBg = (match) => {
    if (match.eventStatus > 0) {
      if (match.matchStatusId === 31) {
        return '-bg-ht';
      } else {
        return '';
      }
    } else {
      if (match.bookingStatus === 2) {
        return '-bg-will-live';
      } else {
        return '-bg-will-no-live';
      }
    }
  };

  const getMatchCardBg = (match) => {
    if (match?.eventStatus > 0) {
      if (match?.matchStatusId === 31) {
        return 'bg-ht';
      } else {
        return 'bg-will-live';
      }
    } else {
      return 'bg-will-no-live';
    }
  };

  const getDayTimedBg = (match) => {
    if (match?.eventStatus > EnumEventStatus.NOTSTARTED) {
      if (match.matchStatusId === EnumMatchStatusName.HALFTIME) {
        return 'bg-dayTime-ht';
      } else {
        return 'bg-dayTime-will-live';
      }
    } else {
      return 'bg-dayTime-will-no-live';
    }
  };

  const handleMatchClick = () => {
    // Save the current scroll position before navigating to match detail
    const scrollableContainer = document.querySelector('.scrollable-y');
    if (scrollableContainer) {
      const scrollPosition = scrollableContainer.scrollTop;
      localStorage.setItem('lastScrollPosition', scrollPosition);
    } else {
      // Fallback to window scroll position
      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      localStorage.setItem('lastScrollPosition', scrollPosition);
    }
  };

  return (
    <Link
      to={{
        pathname: '/match-detail',
        hash: `#${match.providerId}`
      }}
      onClick={handleMatchClick}>
      <div className={'matchCard ' + getMatchCardBg(match)} style={{ flexWrap: 'nowrap' }}>
        <div className="row" xs={8}>
          <Col xs={7} className="dayTime mb-1">
            {match.eventStatus > EnumEventStatus.NOTSTARTED ? (
              <span className={'day2' + getMatchCardDayBg(match)}>
                {t('components.matchcard.live')}
              </span>
            ) : (
              <span className={'day' + getMatchCardDayBg(match)}>
                {gameDateToMatchDay(match.gameDate)}
              </span>
            )}
            {match.eventStatus > EnumEventStatus.NOTSTARTED ? (
              match.sport === 0 ? (
                getMatchTime(match, t)
              ) : (
                <span className="time">
                  {t(`components.matchcard.time.byMatchStatusId.${match.matchStatusId}`)}
                </span>
              )
            ) : (
              <span className="time">{gameDateToMatchTime(match.gameDate)}</span>
            )}
          </Col>
        </div>
        <Row className="teams_wrapper">
          <Col 
            xs={1} 
            className="d-flex align-items-start justify-content-center pr-0 pt-1"
            style={{ borderRight: '1px solid rgba(255, 255, 255, 0.5)', paddingRight: '8px' }}>
            {match.isFavorite ? (
              <img
                onClick={handleFavoriteClick}
                className="star-icon"
                style={{ cursor: 'pointer', width: '20px', height: '20px', zIndex: 10 }}
                src="/icons/star-fill-green.svg"
                alt="Remove from favorites"
              />
            ) : (
              <img
                onClick={handleFavoriteClick}
                className="star-icon"
                style={{ cursor: 'pointer', width: '20px', height: '20px', zIndex: 10 }}
                src={`${AWS_BUCKET_URL}/Icons/starempty.svg`}
                alt="Add to favorites"
              />
            )}
          </Col>
          <Col
            xs={props.marketType === 14 ? 4 : 5}
            className="pr-0 text-color-white"
            style={{ display: 'grid', paddingLeft: '8px' }}>
            {getTeamSolo('team1')}
            {getTeamSolo('team2')}
          </Col>
          <Col
            xs={1}
            className={`score-card pl-0 ${
              match.sport === 2 || (match.sport === 13 && 'tenis-fs')
            }`}>
            {getScore('team1')}
            {getScore('team2')}
          </Col>
          <Col xs={6} className="pl-0 gutter-0-5" style={{ position: 'relative', paddingRight: 2 }}>
            {MatchCardGetTitle()}
          </Col>
        </Row>
      </div>
    </Link>
  );
}
export default MatchCard;
